<template>
  <div>
    <header>
      <v-container>
        <v-row no-gutters>
          <h1 class="font-weight-light headline">
            {{ $t("title.app") }}
          </h1>
          <v-spacer></v-spacer>
          <v-lazy v-if="isDownloadsLoaded" transition="fade-transition">
            <v-text-field v-model="search" append-icon="mdi-magnify" :label="$t('general.search')" single-line dense
              hide-details style="max-width: 200px" class="mr-5 pr-5"></v-text-field>
          </v-lazy>
          <v-lazy transition="fade-transition" v-if="$store.state.tokenData.user.user.tenant_id == '1'">
            <v-btn color="success" dark small @click.stop="openAddDialog()">{{ $t("general.add") }}</v-btn>
          </v-lazy>
        </v-row>
      </v-container>
    </header>
    <section v-if="latestVersion && isDownloadsLoaded" class="py-3">
      <v-container>
        <v-card outlined>
          <v-card-title class="primary--text">
            {{ $t("app.lastest_version") }}
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="6" md="3">
                <strong>{{ $t("app.version") }}:</strong> {{ latestVersion.version }}
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <strong>{{ $t("app.release_date") }}:</strong> {{ formatDate(latestVersion.release_date) }}
              </v-col>
              <v-col cols="12" sm="12" md="6" class="d-flex justify-end">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn class="purple--text custom-hover text-decoration-none" 
                      style="list-style: none;"
                      color="white"
                      @click="downloadLatestApk()"
                      v-on="on">
                      <v-icon left>mdi-download</v-icon>
                      {{ $t("app.download") }}
                    </v-btn>
                  </template>
                  <span>{{ $t("app.download") }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-container>
    </section>

    <section class="py-7">
      <v-container>
        <v-sheet class="px-3 pt-3 pb-3" v-if="!isDownloadsLoaded">
          <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
        </v-sheet>
        <v-card v-else>
          <v-data-table :headers="filteredHeaders()" :items="downloads" :search="search">
            <template v-slot:item.action="{ item }">
              <div style="display: flex">
                <v-tooltip top v-if="$store.state.tokenData.user.user.tenant_id == '1'">
                  <template v-slot:activator="{ on }">
                    <v-icon size="20px" color="blue" class="mr-2" @click="editDownload(item)"
                      v-on="on">mdi-pencil-outline</v-icon>
                  </template>
                  <span>{{ $t("general.edit") }}</span>
                </v-tooltip>
                <v-tooltip top v-if="$store.state.tokenData.user.user.tenant_id == '1'">
                  <template v-slot:activator="{ on }">
                    <v-icon size="22px" color="red" class="mr-2" @click="confirmDelete(item)"
                      v-on="on">mdi-delete-outline</v-icon>
                  </template>
                  <span>{{ $t("general.delete") }}</span>
                </v-tooltip>
              </div>
            </template>

            <template v-slot:item.release_date="{ item }">
              {{ formatDate(item.release_date) }}
            </template>

            <template v-slot:item.date_change="{ item }">
              {{ item.date_change }}
            </template>

            <template v-slot:item.created_at="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template v-slot:item.apk_file="{ item }">
              <v-btn
                small
                class="purple--text custom-hover text-decoration-none" 
                style="list-style: none;"
                color="white"
                @click="downloadApk(item.id)"
              >
                <v-icon left small>mdi-download</v-icon>
                {{ $t("app.download") }}
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
        
        <!-- Download form dialog -->
        <v-dialog v-model="dialog.downloadForm" max-width="600px">
          <v-card>
            <v-card-title>
              <span>{{ formTitle }}</span>
            </v-card-title>

            <v-card-text>
              <v-form ref="form" v-model="valid">
                <v-container>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field 
                        v-model="editedItem.version" 
                        :label="$t('app.version')" 
                        :rules="[rules.required]"
                        outlined 
                        dense
                        autofocus
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-file-input 
                        v-model="editedItem.apk_file" 
                        :label="$t('app.apk')" 
                        :rules="isDownloadEdited ? [] : [rules.required]"
                        accept=".apk" 
                        outlined 
                        dense 
                        show-size 
                        prepend-icon="mdi-file-upload"
                        :hint="isDownloadEdited ? $t('app.optional_if_updating') : ''"
                        persistent-hint
                      ></v-file-input>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-menu 
                        ref="releaseMenu" 
                        v-model="releaseMenu" 
                        :close-on-content-click="false"
                        transition="scale-transition" 
                        offset-y 
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field 
                            :value="formattedReleaseDate" 
                            :label="$t('app.release_date')" 
                            readonly 
                            outlined
                            dense 
                            v-bind="attrs" 
                            v-on="on" 
                            :rules="[rules.required]"
                          ></v-text-field>
                        </template>
                        <v-date-picker 
                          v-model="editedItem.release_date" 
                          @input="releaseMenu = false"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-textarea>
                        v-model="editedItem.date_change" 
                        :label="$t('general.description')" 
                                 
                        outlined 
                        dense
                        :rules="[rules.required]"
                      ></v-textarea> 
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey darken-1" text @click="closeDialog">
                {{ $t("general.cancel") }}
              </v-btn>
              <v-btn 
                color="primary" 
                @click="save" 
                :disabled="!valid"
                :loading="loading"
              >
                {{ $t("general.save") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        
        <!-- Delete confirmation dialog -->
        <v-dialog v-model="dialog.confirmation" max-width="400px">
          <v-card>
            <v-card-title class="text-h5">
              {{ $t("app.delete_version") }}
            </v-card-title>
            <v-card-text>
              {{ $t("app.delete_question") }} {{ selectedDownload.version }}?
              {{ $t("app.delete_action") }}
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey darken-1" text @click="cancelDelete">
                {{ $t("general.cancel") }}
              </v-btn>
              <v-btn 
                color="error" 
                @click="deleteDownload"
                :loading="loading"
              >
                {{ $t("general.delete") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <SnackMessage ref="SnackMessage" />
      </v-container>
    </section>
  </div>
</template>

<script>
import SnackMessage from "../components/SnackMessage";

export default {
  name: 'AppDownloadsTable',

  components: {
    SnackMessage
  },

  data() {
    return {
      downloads: [],
      latestVersion: null,
      search: '',
      valid: false,
      loading: false,
      isDownloadsLoaded: false,
      dialog: {
        confirmation: false,
        downloadForm: false
      },
      releaseMenu: false,
      selectedDownload: {
        id: null,
        version: ''
      },

      editedIndex: -1,
      isDownloadEdited: false,

      editedItem: {
        id: null,
        version: '',
        apk_file: null,
        release_date: new Date().toISOString().substr(0, 10),
        date_change: ''
      },

      defaultItem: {
        id: null,
        version: '',
        apk_file: null,
        release_date: new Date().toISOString().substr(0, 10),
        date_change: ''
      },

      rules: {
        required: v => !!v || this.$t("general.required"),
        url: v => {
          const pattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
          return pattern.test(v) || this.$t("general.invalid_url");
        }
      }
    }
  },

  computed: {
    formTitle() {
      return this.isDownloadEdited ? this.$t('app.edit_version') : this.$t('app.add_version');
    },
    formattedReleaseDate() {
      return this.formatDate(this.editedItem.release_date, true);
    }
  },

  created() {
    this.fetchDownloads();
    this.fetchLatestVersion();
  },

  methods: {
    // Handle date format consistently
    ensureValidDateFormat(dateStr) {
      if (!dateStr) return new Date().toISOString().substr(0, 10);
      
      try {
        // If already in YYYY-MM-DD format, return as is
        if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        
        // Handle ISO format
        if (typeof dateStr === 'string' && dateStr.includes('T')) {
          return dateStr.split('T')[0];
        }
        
        // Handle DD.MM.YYYY format
        if (typeof dateStr === 'string' && dateStr.includes('.')) {
          const parts = dateStr.split(' ')[0].split('.');
          if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
          }
        }
        
        // Try parsing as date object
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().substr(0, 10);
        }
        
        return new Date().toISOString().substr(0, 10);
      } catch (error) {
        console.error('Error formatting date:', error);
        return new Date().toISOString().substr(0, 10);
      }
    },
    
    // Format date for display
    formatDate(dateString, dateOnly = false) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format date as dd.mm.yyyy
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      
      if (dateOnly) {
        return `${day}.${month}.${year}`;
      }
      
      // Include time if not dateOnly
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },
    
    // API calls
    async fetchDownloads() {
      this.isDownloadsLoaded = false;
      try {
        const response = await axios.get('/downloads', {
          headers: {
            Authorization: 'Bearer ' + this.$store.state.tokenData.user.access_token
          }
        });
        this.downloads = response.data;
        this.isDownloadsLoaded = true;
      } catch (error) {
        console.error('Error fetching downloads:', error);
        this.showSnackMessage(this.$t("general.error_loading_versions") || "Error loading versions", "error");
      }
    },
    
    async fetchLatestVersion() {
      try {
        const response = await axios.get('/downloads/latest', {
          headers: {
            Authorization: 'Bearer ' + this.$store.state.tokenData.user.access_token
          }
        });
        this.latestVersion = response.data;
      } catch (error) {
        console.error('Error fetching latest version:', error);
      }
    },
    
    async downloadApk(id) {
      try {
        const url = `/downloads/apk/${id}`;
        this.downloadFile(url);
      } catch (error) {
        console.error('Error downloading APK:', error);
        this.showSnackMessage(this.$t("general.download_error") || "Error downloading file", "error");
      }
    },
    
    async downloadLatestApk() {
      try {
        const url = `/downloads/latestApk`;
        this.downloadFile(url);
      } catch (error) {
        console.error('Error downloading latest APK:', error);
        this.showSnackMessage(this.$t("general.download_error") || "Error downloading file", "error");
      }
    },
    
   async downloadFile(url) {
  try {
    const response = await fetch(url, {
      headers: {
        Authorization: 'Bearer ' + this.$store.state.tokenData.user.access_token
      }
    });

    if (!response.ok) throw new Error('Network response was not ok');

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = "download.apk";

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+)"?/);
      if (match && match[1]) {
        fileName = match[1];
      }
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
    this.showSnackMessage(this.$t("general.download_error") || "Error downloading file", "error");
  }
},

    openAddDialog() {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.isDownloadEdited = false;
      this.dialog.downloadForm = true;
    },
    
    editDownload(item) {
      try {
        this.editedIndex = this.downloads.findIndex(download => download.id === item.id);
        this.editedItem = {
          id: item.id,
          version: item.version || '',
          apk_file: null,
          release_date: this.ensureValidDateFormat(item.release_date),
          date_change: item.date_change || ''
        };
        this.selectedDownload = { 
          id: item.id,
          version: item.version
        };
        this.dialog.downloadForm = true;
        this.isDownloadEdited = true;
      } catch (error) {
        console.error('Error in editDownload:', error);
        this.showSnackMessage(this.$t("general.error_editing") || "Error editing item", "error");
      }
    },

    closeDialog() {
      this.dialog.downloadForm = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        if (this.$refs.form) {
          this.$refs.form.reset();
        }
      });
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      
      this.loading = true;
      
      const formData = new FormData();
      formData.append('version', this.editedItem.version);
      formData.append('release_date', this.editedItem.release_date);
      formData.append('date_change', this.editedItem.date_change);

      if (this.editedItem.apk_file) {
        formData.append('apk_file', this.editedItem.apk_file);
      }

      try {
        const url = this.isDownloadEdited
          ? `/downloads/${this.editedItem.id}`
          : '/downloads';
        
        const config = {
          headers: {
            Authorization: 'Bearer ' + this.$store.state.tokenData.user.access_token,
            'Content-Type': 'multipart/form-data'
          }
        };
        
        let response;
        if (this.isDownloadEdited) {
          // For PUT requests with FormData, many APIs require special handling
          response = await axios.post(`${url}?_method=PUT`, formData, config);
        } else {
          response = await axios.post(url, formData, config);
        }
        
        this.showSnackMessage(
          this.isDownloadEdited 
            ? this.$t("app.version_updated") || "Version updated successfully" 
            : this.$t("app.version_created") || "Version created successfully", 
          "success"
        );
        
        this.closeDialog();
        await this.fetchDownloads();
        await this.fetchLatestVersion();
      } catch (error) {
        console.error('Error saving version:', error);
        this.showSnackMessage(
          this.$t("general.error_saving") || "Error saving version", 
          "error"
        );
      } finally {
        this.loading = false;
      }
    },

    confirmDelete(item) {
      this.selectedDownload = Object.assign({}, item);
      this.dialog.confirmation = true;
    },

    cancelDelete() {
      this.dialog.confirmation = false;
    },

    async deleteDownload() {
      this.loading = true;
      
      try {
        await axios.delete(`/downloads/${this.selectedDownload.id}`, {
          headers: {
            Authorization: 'Bearer ' + this.$store.state.tokenData.user.access_token
          }
        });

        this.showSnackMessage(
          this.$t("app.version_deleted") || "Version deleted successfully", 
          "success"
        );
        
        this.dialog.confirmation = false;
        await this.fetchDownloads();
        await this.fetchLatestVersion();
      } catch (error) {
        console.error('Error deleting download:', error);
        this.showSnackMessage(
          this.$t("general.error_deleting") || "Error deleting version", 
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    
    // Helper methods
    filteredHeaders() {
      const headers = [
        { text: this.$t('app.version'), value: 'version', align: 'left', sortable: true },
        { text: this.$t('app.apk'), value: 'apk_file', align: 'left', sortable: false },
        { text: this.$t('app.release_date'), value: 'release_date', align: 'left', sortable: true },
        { text: this.$t('general.description'), value: 'date_change', align: 'left', sortable: false },
        { text: this.$t('general.created_at'), value: 'created_at', align: 'left', sortable: true }
      ];

      if (this.$store.state.tokenData.user.user.tenant_id == '1') {
        headers.unshift({ text: this.$t('general.actions'), value: 'action', align: 'left', sortable: false });
      }

      return headers;
    },
    
    showSnackMessage(message, color) {
      if (this.$refs.SnackMessage) {
        this.$refs.SnackMessage.showMessage(message, color);
      } else {
        console.warn('SnackMessage component not available');
        this.$emit("snackMessage", message, color);
      }
    }
  }
}
</script>

<style scoped>
.custom-hover:hover {
  background-color: #f5f5f5;
}
</style>